import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Table from '../../components/Table/Table';
import { getCustomTrainings, deleteCustomTraining } from '../../features/customTrainings/services/customTrainingsService';
import BackDropLoader from '../../components/BackDropLoader';
import CustomTrainingWorkoutsInfoModal from '../../features/customTrainings/components/CustomTrainingWorkoutsInfoModal';
import DeleteConfirmationModal from '../../features/customTrainings/components/DeleteConfirmationModal';
import { useAuth } from '../../contexts/authContext';
import CustomTrainingsBlock from '../../features/customTrainings/components/CustomTrainingsBlock';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

export default function CustomTrainingsListPage() {
  const auth = useAuth();
  const intl = useIntl();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [trainingsData, setTrainingsData] = useState({ isFetching: true, trainings: [] });
  const [trainingInfoModalOpen, setTrainingInfoModalOpen] = useState(false);
  const [rowWorkouts, setRowWorkouts] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCustomTraining, setSelectedCustomTraining] = useState(null);

  const fetchCustomTrainings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCustomTrainings(auth.user.id);
      setTrainingsData({ isFetching: false, trainings: data });
      enqueueSnackbar(<FormattedMessage id="customTrainings.get.msg.success" defaultMessage="Custom trainings fetched successfully." />, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id="customTrainings.get.msg.error" defaultMessage="Failed to fetch custom trainings." />, {
        variant: 'error',
      });
      setTrainingsData({ isFetching: false, trainings: [] });
    } finally {
      setLoading(false);
    }
  }, [auth.user.id, enqueueSnackbar]);

  const handleDeleteClick = (training) => {
    setSelectedCustomTraining(training);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedCustomTraining(null);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await deleteCustomTraining(selectedCustomTraining.id);

      enqueueSnackbar(<FormattedMessage id="customTrainings.delete.msg.success" defaultMessage="Custom training deleted successfully." />, {
        variant: 'success',
      });

      fetchCustomTrainings();

      setIsDeleteModalOpen(false);
      setSelectedCustomTraining(null);
    } catch (error) {
      console.error('Error deleting training:', error);
      enqueueSnackbar(<FormattedMessage id="customTrainings.delete.msg.error" defaultMessage="Failed to delete custom training." />, {
        variant: 'error',
      });
    }
  };

  const handleInfoClick = (training) => {
    setRowWorkouts(training.workouts);
    setTrainingInfoModalOpen(true);
  };

  useEffect(() => {
    fetchCustomTrainings();
  }, [fetchCustomTrainings]);

  const trainingDataFormatted = trainingsData.trainings.map((training) => ({
    id: training.id,
    name: training.name,
    description: training.description,
    createdAt: new Date(training.createdAt).toLocaleString(),
    workouts: training.workouts,
  }));

  return (
    <>
      <BackDropLoader open={loading} />
      <Stack spacing={2}>
        <Typography variant="h4" gutterBottom>
          <FormattedMessage id="customTrainings.title" defaultMessage="Custom Trainings" />
        </Typography>
        {isSmallScreen ? (
          <CustomTrainingsBlock
            trainings={trainingDataFormatted}
            onInfoClick={handleInfoClick}
            onDeleteClick={handleDeleteClick}
          />
        ) : (
          <Table
            columns={[
              { title: intl.formatMessage({ id: 'customTrainings.label.name', defaultMessage: 'Name' }), field: 'name' },
              { title: intl.formatMessage({ id: 'customTrainings.label.description', defaultMessage: 'Description' }), field: 'description' },
              { title: intl.formatMessage({ id: 'customTrainings.label.createdAt', defaultMessage: 'Created At' }), field: 'createdAt', type: 'datetime' },
            ]}
            data={trainingDataFormatted}
            actions={[
              {
                icon: () => <InfoIcon color="primary" />,
                tooltip: intl.formatMessage({ id: 'customTrainings.tooltip.info', defaultMessage: 'View Info' }),
                onClick: (event, row) => handleInfoClick(row),
              },
              {
                icon: () => <DeleteIcon color="error" />,
                tooltip: intl.formatMessage({ id: 'customTrainings.tooltip.delete', defaultMessage: 'Delete Training' }),
                onClick: (event, rowData) => handleDeleteClick(rowData),
              },
            ]}
            isLoading={trainingsData.isFetching}
          />
        )}
      </Stack>

      <CustomTrainingWorkoutsInfoModal
        isOpen={trainingInfoModalOpen}
        onClose={() => setTrainingInfoModalOpen(false)}
        workouts={rowWorkouts}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteConfirmed}
        message={'Are you sure you want to delete this training? This will delete the associated Trainings that were created with this custom training.'}
      />
    </>
  );
}
